import { DevicesStore } from './../../../../@store/devices/devices.store';
import { Component, OnInit, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { combineLatest, map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';

import {
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  IsNumberValueProperty,
  IsStringValueProperty,
  PowerLimitationTooltip,
  WallboxChargingPhases,
  WallboxSessionCharging,
  WallboxStatus,
  WallboxStatuses,
  WallboxSubstatus,
} from '@vi-iot/client';

import { InstallationFeaturesStore } from '@store';

import { SUFFIX } from '../../../../shared/common/units';
import { FeaturesDetailsOverlayTypeEnum } from '../../installation-overlay.model';
import { InstallationDetailsMappingService } from 'src/app/+installations/services/installation-details-mapping.service';
import { WallboxSystem } from '../wallbox-system-helper';

@Component({
  selector: 'app-wallbox-system',
  templateUrl: './wallbox-system.component.html',
  styleUrls: ['./wallbox-system.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WallboxSystemComponent implements OnInit, OnDestroy {
  readonly SUFFIX = SUFFIX;
  readonly FeaturePropertyKey = FeaturePropertyKey;
  readonly wallboxStatuses = WallboxStatuses;

  chargingPower$: Observable<Feature<IsNumberValueProperty, never>>;
  displayedStatus$: Observable<Feature<WallboxStatus, never>>;
  displayedSubstatus$: Observable<Feature<WallboxSubstatus, never>>;
  sessionCharging$: Observable<Feature<WallboxSessionCharging, never>>;
  sessionPhases$: Observable<Feature<IsStringValueProperty, never>>;
  availableOverlays = FeaturesDetailsOverlayTypeEnum;
  chargingTranslationKey = '+INSTALLATIONS.INSTALLATION.WALLBOX.CHARGING.';
  phasesTranslationKey = '+INSTALLATIONS.INSTALLATION.WALLBOX.PHASES.NONE';

  displayedStatusHeading: string;
  displayedStatusSubheading: string;
  powerLimitationLabel$: Observable<PowerLimitationTooltip[]>;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  constructor(
    private installationFeaturesStore: InstallationFeaturesStore,
    private installationDetailsMappingService: InstallationDetailsMappingService,
    private devicesStore: DevicesStore
  ) {}

  ngOnInit(): void {
    this.chargingPower$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.wallboxDisplayedPower
    );
    this.displayedStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.wallboxDisplayedStatus
    );
    this.powerLimitationLabel$ = this.devicesStore.selectedDevice$.pipe(
      map((device) => device?.id || null),
      switchMap((deviceId) =>
        deviceId ? this.installationDetailsMappingService.powerLimitationLabel(deviceId) : of(null)
      )
    );
    this.displayedSubstatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.wallboxDisplayedSubstatus
    );
    this.sessionCharging$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.wallboxSessionCharging
    );
    this.sessionPhases$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.wallboxSessionPhases
    );

    combineLatest([this.sessionPhases$, this.displayedStatus$])
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(([sessionPhases, displayedStatus]) => {
        const phase = sessionPhases?.properties?.value?.value;
        const chargingStatus = displayedStatus?.properties?.status?.value;
        if (phase === WallboxChargingPhases.none) {
          this.phasesTranslationKey = '+INSTALLATIONS.INSTALLATION.WALLBOX.PHASES.NONE';

          return;
        }
        this.phasesTranslationKey = WallboxSystem.getChargingPhaseStatusTranslationKey(
          phase,
          chargingStatus
        );
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
